"use client";

import Link from "next/link";
import React, { useMemo, useRef } from "react";
import { Button, Heading, Text, ImageWrapper } from "ui-components";

import CorporateSolutionsLeadForm from "@/components/organization/CorporateSolutionsLeadForm";
import { KnowledgeGraphJsonLd } from "@/components/Schema/KnowledgeGraphJsonLd";
import ContactUsComponent from "@/src/app/[locale]/(public)/about-us/[tab]/components/ContactUsComponent";
import {
  getBreadcrumbSchema,
  getPageSchema,
  getPlatformSchema,
  getWebSiteSchema,
} from "@/utils/schema-generators";

export default function ContactPage(): JSX.Element {
  const targetRef = useRef<HTMLDivElement | null>(null);

  const schemas = useMemo(
    () => [
      getWebSiteSchema(),
      {
        ...getPlatformSchema(),
        foundingDate: 2014,
        slogan:
          "myGwork | LGBTQ+ Talent & Hiring Network | Inclusive Jobs & Employers",
        contactPoint: [
          {
            "@type": "ContactPoint",
            email: "support@mygwork.com",
            contactType: "customer support",
            url: "https://mygwork.com/contact-us",
          },
        ],
      },

      getBreadcrumbSchema(
        [
          { name: "Home", url: "/" },
          { name: "Contact Us", url: "/about-us/contact-us" },
        ],
        "/about-us/contact-us/#breadcrumb"
      ),
      {
        ...getPageSchema({
          url: "/about-us/contact-us/",
          name: "Contact Us | myGwork",
          type: "ContactPage",
          description:
            "Get in touch with the myGwork team for support, corporate solutions, or general inquiries.",
        }),
        mainEntity: { "@id": "https://mygwork.com/#platform" },
        relatedLink: [
          "https://mygwork.atlassian.net/helpcenter/CS",
          "https://mygwork.com/about-us/corporate-solutions",
        ],
        inLanguage: "en",
        dateModified: "2026-01-27",
        potentialAction: {
          "@type": "CommunicateAction",
          name: "Get in touch",
          target: "https://mygwork.com/about-us/contact-us#form",
        },
      },
    ],
    []
  );
  return (
    <div className="flex flex-col gap-y-10">
      <KnowledgeGraphJsonLd schemas={schemas} />
      <div className="flex flex-col max-w-[630px] items-center justify-center gap-5 mx-auto">
        <Heading align="center">Help Center Resources</Heading>
        <Text className="text-center lg:text-left">
          Need help right away? Explore our articles for quick answers to common
          issues on myGwork. For further assistance, you can always reach out to
          us using the form below.
        </Text>
        <Link
          href="https://mygwork.atlassian.net/helpcenter/CS/"
          target="_blank"
        >
          <Button>Visit our Help Center</Button>
        </Link>
      </div>
      <div className="flex flex-col max-w-[630px] items-center justify-center gap-5 mx-auto mt-10">
        <Heading align="center">Want to become a Corporate Partner?</Heading>
        <Text className="text-center lg:text-left">
          Join the growing community of inclusive organizations leveraging our
          360° Recruitment Solutions. Explore partnership opportunities through
          our{" "}
          <Link href="/about-us/corporate-solutions" className="text-primary">
            Corporate Solutions Guide
          </Link>
          , or register your interest in our LGBTQ+ Talent Attraction Platform
          now by clicking below.
        </Text>
        <CorporateSolutionsLeadForm intent="primary" />
      </div>
      <Heading align="center" className="mt-10">
        Get in touch
      </Heading>
      <div
        ref={targetRef}
        className="relative w-full h-[165px] md:w-[400px] md:h-[165px] mx-auto"
      >
        <ImageWrapper src="/about-us/contact.png" alt="contact" fill />
      </div>
      <div className="flex flex-col gap-y-10 md:w-[500px] items-center justify-center md:mx-auto">
        <ContactUsComponent />
      </div>
    </div>
  );
}
